import { useState } from "react";
import { Highlight, themes } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { ArtifactToolbarActions } from "~/components/assistant/messages/ArtifactToolbarActions";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import { DownloadDialog } from "~/components/assistant/messages/download/DownloadDialog";
import { DataValue } from "~/components/assistant/types";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";

interface DataValueMessageProps {
  value: DataValue;
  debugMode?: boolean;
}

type TabType = "value" | "code" | "lineage" | "details";

interface Tab {
  id: TabType;
  label: string;
}

// DataValueMessage component with tabbed interface
export function DataValueMessage({ value, debugMode = false }: DataValueMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("value");
  const [showDownloadDialog, setShowDownloadDialog] = useState(false);
  const { conversationId, contextId } = useAssistantContext();

  // Load Python language support for syntax highlighting
  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: value.artifact_id || undefined,
      artifact_type: "value",
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "value", label: "Value" }];
  if (value?.code) {
    tabs.push({ id: "code", label: "Code" });
  }

  if (value?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  if (debugMode) {
    tabs.push({ id: "details", label: "Details" });
  }

  return (
    <>
      <div
        className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full"
        aria-label="Data value display"
      >
        {/* Tabs */}
        <div
          className="flex items-center border-b border-gray-200 mb-4"
          role="tablist"
          aria-label="Data value tabs"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                "px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer",
                "max-w-20 min-w-0 overflow-hidden whitespace-nowrap text-ellipsis break-normal",
                activeTab === tab.id
                  ? "border-gray-900 text-gray-900"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              )}
            >
              {tab.label}
            </button>
          ))}
          <ArtifactToolbarActions
            onDownload={
              value.artifact_id
                ? () => {
                    setShowDownloadDialog(true);
                    trackEvent("analysis_agent_artifact_download_requested", {
                      dataset_id: contextId || "Unknown",
                      conversation_id: conversationId || undefined,
                      artifact_id: value.artifact_id || undefined,
                      artifact_type: "value",
                    });
                  }
                : undefined
            }
          />
        </div>

        {/* Tab Content */}
        <div className="overflow-x-auto">
          {activeTab === "value" && (
            <div role="tabpanel" id="tabpanel-value" aria-label="Value content">
              <div className="flex items-center justify-end mb-1">
                <CopyButton text={value.content} variant="light" size="sm" />
              </div>
              <div className="bg-white rounded-sm p-4">
                <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap break-words">
                  {value.content}
                </pre>
              </div>
              {value?.summary && <div className="mt-3 text-xs text-gray-600">{value.summary}</div>}
            </div>
          )}

          {activeTab === "code" && value?.code && (
            <div role="tabpanel" id="tabpanel-code" aria-label="Code content">
              {/* Code Header with Copy Button */}
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
                <CopyButton text={value.code} variant="light" size="sm" />
              </div>
              <div className="h-full overflow-hidden rounded-sm">
                {!isLoading && (
                  <Highlight
                    theme={themes.dracula}
                    code={value.code}
                    language="python"
                    prism={PrismInstance as any}
                  >
                    {({ style, tokens, getLineProps, getTokenProps, className }) => (
                      <pre
                        className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                          className || ""
                        }`}
                        style={style}
                      >
                        {tokens.map((line, i) => (
                          <div
                            {...getLineProps({ line, key: i })}
                            key={`line-${i}`}
                            style={{ minHeight: "1.5em" }}
                          >
                            {line.map((token, key) => (
                              <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                            ))}
                          </div>
                        ))}
                      </pre>
                    )}
                  </Highlight>
                )}
                {isLoading && (
                  <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                    Loading syntax highlighting...
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "lineage" && value?.artifact_id && (
            <div role="tabpanel" id="tabpanel-lineage" aria-label="Lineage content">
              <LineageTab artifactId={value.artifact_id} />
            </div>
          )}

          {activeTab === "details" && debugMode && (
            <div role="tabpanel" id="tabpanel-details" aria-label="Details content">
              <div className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800">
                <DetailField label="Value Data" content={value} />
              </div>
            </div>
          )}
        </div>
      </div>

      <DownloadDialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        artifact={value}
        artifactType="value"
      />
    </>
  );
}
